import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center px-4" style={{ backgroundColor: 'var(--background-main)' }}>
      <Card className="w-full max-w-md bg-[var(--background-surface)] border-[var(--border-subtle)]">
        <CardHeader className="text-center">
          <Link to="/" className="inline-block mb-4">
            <h1 className="font-heading text-2xl font-bold text-[var(--text-primary)]">Sudoku Academy</h1>
          </Link>
          <CardTitle className="text-2xl font-heading text-[var(--text-primary)]">
            {sent ? 'Check Your Email' : 'Forgot Password'}
          </CardTitle>
          <CardDescription className="text-[var(--text-secondary)]">
            {sent 
              ? 'If an account exists with that email, we sent a reset link.'
              : 'Enter your email and we\'ll send you a reset link.'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 rounded bg-[#34C759]/10 border border-[#34C759]/20" data-testid="reset-sent-message">
                <CheckCircle className="w-5 h-5 text-[#34C759] flex-shrink-0" />
                <div>
                  <p className="text-sm text-[var(--text-primary)] font-medium">Reset link sent!</p>
                  <p className="text-xs text-[var(--text-secondary)] mt-1">
                    Check your inbox (and spam folder) for an email from Sudoku Academy. The link expires in 1 hour.
                  </p>
                </div>
              </div>
              
              <Button
                variant="outline"
                onClick={() => { setSent(false); setEmail(''); }}
                className="w-full border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--hover-bg)]"
                data-testid="resend-btn"
              >
                Try a different email
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 rounded bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="forgot-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[var(--text-secondary)]">Email address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-disabled)]" />
                  <Input
                    id="email"
                    type="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    inputMode="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-[var(--background-elevated)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-disabled)]"
                    required
                    data-testid="forgot-email"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#8B5A3C] hover:bg-[#A46944] text-white font-semibold"
                disabled={loading}
                data-testid="forgot-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] inline-flex items-center gap-1 transition-colors">
              <ArrowLeft className="w-3 h-3" />
              Back to sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
